import { Plugin, Widget } from 'ckeditor5'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { toWidget } from 'ckeditor5'
import { Jumble } from '@minicoursegenerator/jumble'

// Transform config - API data is already in correct format, just pass through
const transformConfigForJumble = (config) => {
    if (!config) return config
    return { ...config }
}

const DEFAULT_JUMBLE_JSON = {
    config: {
        language: 'en',
        primaryColor: '#015ffb',
        accentColor: '#10b981',
        bgColor: '#f8fafc',
        showAnswer: true
    },
    words: [
        {
            word: 'HELLO',
            scrambled: 'OLLEH',
            definition: 'A common greeting used when meeting someone'
        },
        {
            word: 'WORLD',
            scrambled: 'DLROW',
            definition: 'The earth and all the people on it'
        }
    ]
}

export default class JumblePlugin extends Plugin {
    static get pluginName() {
        return 'JumblePlugin'
    }

    static get requires() {
        return [Widget]
    }

    constructor(editor) {
        super(editor)
        this.mountedRoots = new Map()
        this._updateTimeout = null
        this._lastConfigStr = null
    }

    init() {
        const editor = this.editor

        editor.model.schema.register('jumble', {
            isObject: true,
            allowWhere: '$block',
            allowIn: 'column',
            allowAttributes: [
                'id',
                'data',
                'gameConfig',
            ],
        })

        this._defineConverters()
        this._setupAttributeListener()

        editor.ui.componentFactory.add('insertJumble', locale => {
            const button = document.createElement('button')
            button.className = 'ck-button ck-button_with-text'
            button.style.display = 'flex'
            button.style.alignItems = 'center'
            button.style.gap = '4px'
            button.style.padding = '8px 12px'

            const icon = document.createElement('span')
            icon.textContent = '🔀'
            icon.style.fontSize = '16px'

            const text = document.createTextNode('Jumble')

            button.appendChild(icon)
            button.appendChild(text)

            button.onclick = () => {
                editor.execute('insertJumble')
            }

            return {
                element: button,
                render: () => { },
                destroy: () => { },
            }
        })

        // Command to insert via button (uses default JSON)
        editor.commands.add('insertJumble', {
            execute: () => {
                editor.model.change(writer => {
                    const jumbleLanguage = editor.jumbleLanguage || 'en'
                    const newConfig = JSON.parse(JSON.stringify(DEFAULT_JUMBLE_JSON))
                    if (newConfig.config) {
                        newConfig.config.language = jumbleLanguage
                    }

                    // Get bgColor from config or colors.background
                    const bgColor = newConfig?.config?.bgColor || newConfig?.colors?.background || '#ffffff'

                    const simpleBoxWrapper = writer.createElement('simpleBoxWrapper', {
                        backgroundColor: bgColor,
                    })
                    const simpleBox = writer.createElement('simpleBox')
                    const column = writer.createElement('column')
                    const jumble = writer.createElement('jumble', {
                        gameConfig: JSON.stringify(newConfig),
                    })

                    writer.append(simpleBox, simpleBoxWrapper)
                    writer.append(column, simpleBox)
                    writer.append(jumble, column)

                    editor.model.insertContent(simpleBoxWrapper)
                    writer.setSelection(jumble, 'on')
                })
            },
        })
    }

    _setupAttributeListener() {
        const editor = this.editor

        editor.model.document.on('change:data', () => {
            const changes = editor.model.document.differ.getChanges()

            for (const change of changes) {
                if (change.type === 'attribute' && change.attributeKey === 'gameConfig') {
                    const modelElement = change.range.start.nodeAfter
                    if (modelElement && modelElement.name === 'jumble') {
                        const newGameConfig = modelElement.getAttribute('gameConfig')
                        this._updateJumbleComponent(modelElement, newGameConfig)
                    }
                }
            }
        })
    }

    _updateJumbleComponent(modelElement, gameConfig) {
        if (gameConfig === this._lastConfigStr) {
            return
        }

        this._lastConfigStr = gameConfig

        if (this._updateTimeout) {
            clearTimeout(this._updateTimeout)
        }

        this._updateTimeout = setTimeout(() => {
            const editor = this.editor

            const viewElement = editor.editing.mapper.toViewElement(modelElement)
            if (!viewElement) return

            const domConverter = editor.editing.view.domConverter
            const containerDom = domConverter.mapViewToDom(viewElement)
            if (!containerDom) return

            const placeholder = containerDom.querySelector('.jumble-react-placeholder')
            if (!placeholder) return

            const existingRoot = this.mountedRoots.get(placeholder)
            if (existingRoot) {
                try {
                    const parsedConfig = gameConfig ? JSON.parse(gameConfig) : DEFAULT_JUMBLE_JSON
                    const transformedConfig = transformConfigForJumble(parsedConfig)
                    const configLanguage = transformedConfig?.config?.language || transformedConfig?.language || editor.jumbleLanguage || 'en'

                    const props = {
                        data: transformedConfig,
                        language: configLanguage,
                        onComplete: () => console.log('🔀 Jumble Solved!'),
                        onFail: () => console.log('🔀 Jumble Failed!'),
                        onChange: state => console.log('🔀 Jumble State:', state),
                    }

                    console.log('🔀 Jumble re-rendering with new config:', props)
                    existingRoot.render(React.createElement(Jumble, props))
                } catch (error) {
                    console.error('Error re-rendering Jumble component:', error)
                }
            }
        }, 500)
    }

    _defineConverters() {
        const editor = this.editor
        const conversion = editor.conversion

        conversion.for('upcast').elementToElement({
            view: {
                name: 'div',
                attributes: {
                    'data-type': 'jumble',
                },
            },
            model: (viewElement, { writer }) => {
                const id = viewElement.getAttribute('id')
                const data = viewElement.getAttribute('data') || ''
                const gameConfig = viewElement.getAttribute('data-game-config') || ''

                const attrs = {
                    data,
                    gameConfig,
                }
                if (id) attrs.id = id

                return writer.createElement('jumble', attrs)
            },
        })

        conversion.for('dataDowncast').elementToElement({
            model: 'jumble',
            view: (modelElement, { writer }) => {
                const id = modelElement.getAttribute('id')
                const data = modelElement.getAttribute('data') || ''
                const gameConfig = modelElement.getAttribute('gameConfig') || ''

                const attrs = {
                    'data-type': 'jumble',
                    data: data,
                    'data-game-config': gameConfig,
                }

                if (id !== undefined && id !== null) {
                    attrs.id = id
                }

                return writer.createEmptyElement('div', attrs)
            },
        })

        conversion.for('editingDowncast').elementToElement({
            model: 'jumble',
            view: (modelElement, { writer }) => {
                const gameConfig = modelElement.getAttribute('gameConfig') || ''

                const containerAttrs = {
                    class: 'jumble-widget-container',
                    'data-type': 'jumble',
                    'data-game-config': gameConfig,
                }

                const container = writer.createContainerElement('div', containerAttrs)

                const placeholder = writer.createRawElement(
                    'div',
                    {
                        class: 'jumble-react-placeholder',
                    },
                    domElement => {
                        const stopPropagation = e => e.stopPropagation()
                        const events = ['mousedown', 'click', 'dblclick', 'pointerdown', 'pointermove', 'pointerup', 'keydown', 'keyup', 'input']
                        events.forEach(evt => domElement.addEventListener(evt, stopPropagation))
                        domElement.addEventListener('focus', stopPropagation, true)
                        domElement.addEventListener('blur', stopPropagation, true)

                        console.log('🔀 Jumble rendering started')
                        try {
                            const parsedConfig = gameConfig ? JSON.parse(gameConfig) : DEFAULT_JUMBLE_JSON
                            const transformedConfig = transformConfigForJumble(parsedConfig)
                            const configLanguage = transformedConfig?.config?.language || transformedConfig?.language || editor.jumbleLanguage || 'en'

                            const props = {
                                data: transformedConfig,
                                language: configLanguage,
                                onComplete: () => console.log('🔀 Jumble Solved!'),
                                onFail: () => console.log('🔀 Jumble Failed!'),
                                onChange: state => console.log('🔀 Jumble State:', state),
                            }

                            console.log('🔀 Jumble Props (Initial):', props)

                            const root = createRoot(domElement)
                            root.render(React.createElement(Jumble, props))

                            editor.plugins
                                .get('JumblePlugin')
                                .mountedRoots.set(domElement, root)
                        } catch (error) {
                            console.error('Error rendering Jumble component:', error)
                            domElement.innerHTML = `
                <div style="padding: 20px; background: #f5f5f5; border-radius: 8px; text-align: center;">
                  <span style="font-size: 24px;">🔀</span>
                  <p>Jumble</p>
                  <small>Component not available</small>
                </div>
              `
                        }
                    },
                )

                writer.insert(writer.createPositionAt(container, 0), placeholder)

                return toWidget(container, writer, {
                    label: 'Jumble',
                    hasSelectionHandle: true,
                })
            },
        })
    }
}
